import type { ErrorLog, ErrorPlatform, ErrorSeverity } from "@ai-teach/types";
import { Button } from "@ai-teach/ui-web";
import { useCallback, useEffect, useState } from "react";
import {
  MdDangerous,
  MdError,
  MdExpandLess,
  MdExpandMore,
  MdRefresh,
  MdWarning,
} from "react-icons/md";

interface ErrorsResponse {
  errors: ErrorLog[];
  total: number;
  page: number;
  pageSize: number;
}

export function ErrorsDashboard() {
  const [errors, setErrors] = useState<ErrorLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Filters
  const [platform, setPlatform] = useState<ErrorPlatform | "">("");
  const [severity, setSeverity] = useState<ErrorSeverity | "">("");
  const [days, setDays] = useState("7");
  const [search, setSearch] = useState("");

  // Load errors callback - must be defined before early returns to satisfy Rules of Hooks
  const loadErrors = useCallback(async () => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    if (platform) params.append("platform", platform);
    if (severity) params.append("severity", severity);
    if (days) params.append("days", days);
    if (search) params.append("search", search);
    params.append("limit", "100");

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/errors?${params}`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to load errors");
      }

      const data: ErrorsResponse = await response.json();
      setErrors(data.errors);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load errors");
    } finally {
      setLoading(false);
    }
  }, [platform, severity, days, search]);

  // Load errors when filters change
  useEffect(() => {
    loadErrors();
  }, [loadErrors]);

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const stats = {
    total: errors.length,
    critical: errors.filter((e) => e.severity === "critical").length,
    error: errors.filter((e) => e.severity === "error").length,
    warning: errors.filter((e) => e.severity === "warning").length,
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="h3 mb-6">Error Logs Dashboard</h1>

        {/* Filters */}
        <div className="panel p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label htmlFor="platform" className="block label mb-1">
                Platform
              </label>
              <select
                id="platform"
                value={platform}
                onChange={(e) => setPlatform(e.target.value as ErrorPlatform | "")}
                className="w-full px-3 py-2 bg-surface border border-gray-800 rounded-md text-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All</option>
                <option value="web">Web</option>
                <option value="mobile">Mobile</option>
              </select>
            </div>

            <div>
              <label htmlFor="severity" className="block label mb-1">
                Severity
              </label>
              <select
                id="severity"
                value={severity}
                onChange={(e) => setSeverity(e.target.value as ErrorSeverity | "")}
                className="w-full px-3 py-2 bg-surface border border-gray-800 rounded-md text-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All</option>
                <option value="critical">Critical</option>
                <option value="error">Error</option>
                <option value="warning">Warning</option>
              </select>
            </div>

            <div>
              <label htmlFor="days" className="block label mb-1">
                Time Range
              </label>
              <select
                id="days"
                value={days}
                onChange={(e) => setDays(e.target.value)}
                className="w-full px-3 py-2 bg-surface border border-gray-800 rounded-md text-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="1">Last 24 hours</option>
                <option value="7">Last 7 days</option>
                <option value="14">Last 14 days</option>
              </select>
            </div>

            <div>
              <label htmlFor="search" className="block label mb-1">
                Search
              </label>
              <input
                id="search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search message or type..."
                className="w-full px-3 py-2 bg-surface border border-gray-800 rounded-md text-secondary-200 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="flex items-end">
              <Button
                type="button"
                onClick={loadErrors}
                disabled={loading}
                variant="primary"
                className="w-full"
              >
                <MdRefresh className={loading ? "animate-spin" : ""} />
                {loading ? "Loading..." : "Apply Filters"}
              </Button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="panel p-6">
            <div className="caption mb-1">Total Errors</div>
            <div className="h3">{stats.total}</div>
          </div>
          <div className="panel p-6">
            <div className="flex items-center gap-2 caption mb-1">
              <MdDangerous className="text-error-500" />
              Critical
            </div>
            <div className="text-3xl font-bold text-error-500">{stats.critical}</div>
          </div>
          <div className="panel p-6">
            <div className="flex items-center gap-2 caption mb-1">
              <MdError className="text-warning-500" />
              Errors
            </div>
            <div className="text-3xl font-bold text-warning-500">{stats.error}</div>
          </div>
          <div className="panel p-6">
            <div className="flex items-center gap-2 caption mb-1">
              <MdWarning className="text-primary-600" />
              Warnings
            </div>
            <div className="text-3xl font-bold text-primary-600">{stats.warning}</div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-error-500/10 border border-error-500 text-error-400 rounded-lg p-4 mb-6">
            {error}
          </div>
        )}

        {/* Errors Table */}
        <div className="data-table-container">
          <div className="data-table-header">
            <h2 className="h5">Error Logs</h2>
          </div>
          {loading ? (
            <div className="text-center py-12 text-gray-400">Loading...</div>
          ) : errors.length === 0 ? (
            <div className="text-center py-12 text-gray-400">No errors found</div>
          ) : (
            <div className="overflow-x-auto gradient-scrollbar">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Severity</th>
                    <th>Platform</th>
                    <th>Message</th>
                    <th className="w-32">Screen</th>
                    <th className="w-20">Count</th>
                    <th>User</th>
                  </tr>
                </thead>
                <tbody>
                  {errors.map((err) => (
                    <tr key={err.id}>
                      <td>
                        <span className="data-table-cell-caption">
                          {new Date(err.timestamp).toLocaleString()}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`uppercase ${
                            err.severity === "critical"
                              ? "badge-danger"
                              : err.severity === "error"
                                ? "badge-accent"
                                : "badge-primary"
                          }`}
                        >
                          {err.severity}
                        </span>
                      </td>
                      <td>
                        <span className="badge-secondary">{err.platform}</span>
                      </td>
                      <td>
                        <div className="max-w-md">
                          <button
                            type="button"
                            className="text-sm text-secondary-200 hover:text-primary-600 flex items-center gap-2 w-full text-left transition-colors"
                            onClick={() => toggleExpand(err.id)}
                          >
                            <span className="truncate">{err.message}</span>
                            {err.stack && (
                              <span className="flex-shrink-0">
                                {expandedIds.has(err.id) ? (
                                  <MdExpandLess className="text-gray-400" />
                                ) : (
                                  <MdExpandMore className="text-gray-400" />
                                )}
                              </span>
                            )}
                          </button>
                          {expandedIds.has(err.id) && err.stack && (
                            <pre className="mt-2 text-xs bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto max-h-60">
                              {err.stack}
                            </pre>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="data-table-cell-caption truncate block max-w-32">
                          {err.screen}
                        </span>
                      </td>
                      <td>
                        {err.count > 1 ? (
                          <span className="badge-danger">{err.count}x</span>
                        ) : (
                          <span className="data-table-cell-caption">1</span>
                        )}
                      </td>
                      <td>
                        <span className="data-table-cell-caption">{err.userId || "anonymous"}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
